import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  Alert,
  Chip
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useUpdateCategory } from "../../hooks/api/useCategory";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { Category, CategoryFormData } from "./types/categoryTypes";
import useMediaQuery from "@mui/material/useMediaQuery";

const getSchema = (t: (key: string, fallback: string) => string) =>
  Yup.object().shape({
    name: Yup.string().required(
      t("subcategory-name-required", "Subcategory name is required")
    ),
    description: Yup.string().optional(),
    parentId: Yup.string().optional()
  }) as Yup.ObjectSchema<CategoryFormData>;

interface EditSubcategoryModalProps {
  open: boolean;
  onClose: () => void;
  subcategory: Category;
  parentCategory?: Category;
  onSuccess?: () => void | Promise<void>;
}

const EditSubcategoryModal: React.FC<EditSubcategoryModalProps> = ({
  open,
  onClose,
  subcategory,
  parentCategory,
  onSuccess
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const updateCategory = useUpdateCategory();
  const { t } = useKivunovaTranslation();

  const schema = getSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<CategoryFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: subcategory.name,
      description: subcategory.description,
      parentId: subcategory.parentId ?? null,
      companyId: subcategory.companyId,
      createdBy: subcategory.createdBy
    }
  });

  const onSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    try {
      await updateCategory.mutateAsync({
        id: subcategory.id,
        name: data.name,
        description: data.description ?? undefined,
        parentId: data.parentId ?? undefined,
        status: subcategory.status
      });
      await onSuccess?.();
      onClose();
    } catch (error) {
      console.error("❌ Failed to update subcategory:", error);
    }
  };

  const isLoading = isSubmitting || updateCategory.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Box>
            <Typography
              variant={matchMD ? "h6" : "h5"}
              fontWeight="bold"
              color="textPrimary"
            >
              {t("edit-subcategory", "Edit Subcategory")}
            </Typography>
            {parentCategory && (
              <Typography variant="caption" color="text.secondary">
                {t("parent", "Parent")}: {parentCategory.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Subcategory Info Banner */}
        {parentCategory && (
          <Box sx={{ p: matchMD ? 2 : 3, pb: 0 }}>
            <Alert severity="info" icon={<FolderIcon />} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {t("editing", "Editing")}: {subcategory.name}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                  {t("path", "Path")}: {subcategory.path || "-"}
                </Typography>
                {subcategory.depth !== undefined && (
                  <Box mt={0.5}>
                    <Chip
                      label={`${t("depth", "Depth")}: ${t("level", "Level")} ${subcategory.depth}`}
                      size="small"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  </Box>
                )}
              </Box>
            </Alert>
          </Box>
        )}

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#555" }
            }
          }}
        >
          {/* Form - Vertical Layout */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("subcategory-name", "Subcategory Name")}
                  placeholder={t(
                    "subcategory-name-placeholder",
                    "Enter subcategory name"
                  )}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  autoFocus
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("description", "Description")}
                  placeholder={t(
                    "subcategory-description-placeholder",
                    "Enter subcategory description"
                  )}
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                />
              )}
            />

            {parentCategory && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  {t(
                    "edit-subcategory-parent-warning",
                    "Note: Changing the parent category will move this subcategory and all its children to a new location in the hierarchy."
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        </Box>

        {/* Fixed Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save-changes", "Save Changes")}
          </LoadingButton>

          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditSubcategoryModal;
